<template>
  <div class="gmf-compact-leaderboard-section gmf-context">
    <template v-if="selectedLeaderboard">
      <div class="gmf-compact-leaderboard-container">
        <div
            v-for="row in leaderboardRows"
            :key="row.customerId"
            class="gmf-compact-leaderboard-item">
            <div class="gmf-compact-leaderboard-avatar">
              <img class="gmf-compact-leaderboard-icon" v-if="row.rank == 1" src="https://cdn.gamifiera.com/static/icons/trophy_gold.svg" />
              <img class="gmf-compact-leaderboard-icon" v-if="row.rank == 2" src="https://cdn.gamifiera.com/static/icons/trophy_silver.svg" />
              <img class="gmf-compact-leaderboard-icon" v-if="row.rank == 3" src="https://cdn.gamifiera.com/static/icons/trophy_bronze.svg" />
              <Avatar :customer="customerObject(row)" />
            </div>
            <div class="gmf-compact-leaderboard-customer-name">
              <CustomerName
                class="gmf-compact-leaderboard-customer-name gmf-subheader"
                :customer="{
                  id: row.customerId,
                  publicProfile: true,
                  isGuest: false,
                  firstName: row.customerName,
                }"
              />
              <CustomerLevelRow :customer="customerObject(row)" />

              <div class="gmf-compact-leaderboard-score">{{ niceThousands(row.score) }} {{ translatedBoardName }}</div>
            </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style>
.gmf-compact-leaderboard-section {
  width: 100%;
}
.gmf-compact-leaderboard-title {
  margin-top: 0px;
  text-align: center;
}
.gmf-compact-leaderboard-container {
  width: 100%;
  display: flex;
  margin-top: 0px;
  border-spacing: 0;
  gap: var(--gmf-p-4);
  flex-wrap: wrap-reverse;
  justify-content: space-evenly;
  flex-direction: row-reverse;
  color: var(--gmf-col-black);
}
.gmf-compact-leaderboard-item {
  display: flex;
  min-width: 230px;
  max-width: 300px;
  gap: var(--gmf-p-3);
  align-items: center;
  flex-direction: row;
}
.gmf-compact-leaderboard-avatar {
  position: relative;
}
.gmf-compact-leaderboard .gmf-avatar {
  width: 80px;
  height: 80px;
  max-width: unset;
  max-height: unset;
}
@media (max-width: 480px) {
  .gmf-compact-leaderboard .gmf-avatar {
    width: 64px;
    height: 64px;
  }
}
@media (max-width: 600px) {
  .gmf-compact-leaderboard-item {
    width: 100%;
  }
}
.gmf-compact-leaderboard-icon {
  top: 100%;
  z-index: 1;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  position: absolute;
  transform: translate(-25%, -100%);
}
.gmf-compact-leaderboard-customer-name.gmf-subheader {
  flex-shrink: 0;
  margin-bottom: 0;
}
.gmf-compact-leaderboard-item .gmf-level-row {
  flex-direction: column;
  align-items: flex-start;
  gap: var(--gmf-p-1);
}
.gmf-compact-leaderboard .gmf-compact-leaderboard-score {
  flex-shrink: 0;
  white-space: nowrap;
  margin-top: var(--gmf-p-2);
  color: var(--gmf-brand-color);
}
</style>

<script>
import state from '@/state';
import callApi from '@/utils/callApi';
import Avatar from '@/components/Avatar.vue';
import CustomerName from '@/components/CustomerName';
import CustomerLevelRow from '@/components/CustomerLevelRow';

const t = state.t.prefix('leaderboards');

export default {
  inject: ['injectedLeaderboard'],
  components: {
    Avatar,
    CustomerName,
    CustomerLevelRow,
  },
  data () {
    return {
      state,

      page: 1,
      itemsPerPage: 3,
      leaderboardRows: [],

      leaderboards: [],
      selectedPeriod: null,
      selectedLeaderboard: null,

      isLoading: false,
    };
  },

  computed: {
    translatedBoardName() {
      return this.t(`boards.${this.selectedLeaderboard.name}`).toLowerCase()
    }
  },

  async mounted () {
    if (this.injectedLeaderboard) {
      let injectedData = this.injectedLeaderboard.split(':');
      this.selectedLeaderboard = { name: injectedData[0] };
      this.selectedPeriod = injectedData[1]; // Will trigger load
      return;
    }

    const listRes = await callApi('GET', '/v1/client/leaderboards');
    const { leaderboards } = await listRes.json();

    this.leaderboards = leaderboards;
    if (leaderboards.length > 0) {
      this.selectedLeaderboard = leaderboards[0];
      this.selectedPeriod = this.selectedLeaderboard.periods[0]; // Will trigger load
    }
  },
  methods: {
    t,
    niceThousands (value) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    reset () {
      this.isLoading = false;
      this.leaderboardRows = [];
      this.page = 1;
    },
    async load () {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;

      const res = await callApi('GET', '/v1/client/leaderboards/' + this.selectedLeaderboard.name + '?period=' + this.selectedPeriod + '&take=3');
      const { rows, error } = await res.json();

      if (error) {
        switch (error.code) {
          case 804003:
            console.error("Failed to load leaderboard in gmf-compact-leaderboard, leaderboard does not exist:", this.selectedLeaderboard.name);
            break;
          case 804004:
            console.error("Failed to load leaderboard in gmf-compact-leaderboard, invalid time period:", this.selectedPeriod);
            break;
          default:
            console.error("Failed to load leaderboard in gmf-compact-leaderboard.");
        }
        this.isLoading = false;
        return;
      }

      this.leaderboardRows.push(...rows);
      this.leaderboardRows = this.leaderboardRows.reverse();

      this.page++;

      this.isLoading = false;
    },
    customerObject (row) {
      return {
        id: row.customerId,
        name: row.customerName,
        level: row.customerLevel,
        avatarUrl: row.avatarUrl,
        levelName: row.customerLevelName,

        publicProfile: true,
        isOnline: row.isOnline,
        lastOnline: row.lastOnline,
      }
    }
  },
  watch: {
    selectedPeriod () {
      this.reset();
      this.load();
    },
  },
};
</script>
